import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import { useLocation } from 'react-router-dom';

import { motion } from '@/theme';

/**
 * A short fade-and-rise when the route changes (spec §9.3).
 *
 * Done with a CSS animation keyed on the pathname rather than with
 * framer-motion: this is one 160ms fade, and the library is ~35 KB gzipped
 * against a Lighthouse performance budget the spec also sets. The `key` is what
 * restarts the animation — React remounts the subtree, which is also what
 * resets scroll-restoration behaviour to the top of the new page.
 *
 * `prefers-reduced-motion` removes the movement entirely rather than shortening
 * it: for someone with vestibular sensitivity a fast slide is worse than none.
 */
export function PageTransition({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  return (
    <Box
      key={pathname}
      sx={{
        '@media (prefers-reduced-motion: no-preference)': {
          animation: `page-enter ${motion.base}ms ${motion.easing} both`,
        },
        '@keyframes page-enter': {
          from: { opacity: 0, transform: 'translateY(4px)' },
          to: { opacity: 1, transform: 'none' },
        },
      }}
    >
      {children}
    </Box>
  );
}
